import type { Components, Theme } from "@mui/material/styles";
import { lighten } from "@mui/material/styles";

export const INPUT_PADDING_SPACING = 1.5;

export const MuiInputBase: Components<Theme>["MuiInputBase"] = {
  styleOverrides: {
    root: ({ theme }) => {
      const errorBackground = lighten(theme.palette.error.light, 0.72);

      return {
        backgroundColor: theme.palette.common.white,
        fontSize: "0.875rem",
        gap: theme.spacing(1),
        height: theme.spacing(5),
        minHeight: theme.spacing(5),
        "&&": {
          padding: theme.spacing(0, INPUT_PADDING_SPACING),
        },

        "label + &&": {
          marginTop: 0,
        },

        "&.Mui-error": {
          background: errorBackground,
        },

        "&&&.MuiInputBase-adornedStart": {
          height: "100%",
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
        },

        /**
         * Browsers apply a background to input elements when using autofill - this
         * looks awkward when in an error state or when using adornments because the
         * adornments are outside the input element and retain their original
         * background colors. This special input background color cannot be easily
         * overriden, but workarounds have been done using box shadow to cover the
         * background color.
         */
        "& input:-webkit-autofill": {
          boxShadow: `0 0 0px 1000px ${theme.palette.common.white} inset !important`,
          transition: "background-color 5000s ease-in-out 0s",
        },

        /**
         * If in an error state, override with our error background as a box shadow.
         **/
        "&.Mui-error input:-webkit-autofill": {
          boxShadow: `0 0 0px 1000px ${errorBackground} inset !important`,
        },
      };
    },

    input: {
      height: "100%",
      "&&": {
        padding: 0,
      },
    },

    inputAdornedStart: {
      flex: 1,
    },

    multiline: ({ theme }) => ({
      "&&": {
        height: "unset",
        "& textarea": {
          padding: theme.spacing(INPUT_PADDING_SPACING, 0),
        },
      },
    }),

    sizeSmall: ({ theme }) => ({
      height: theme.spacing(4),
      minHeight: theme.spacing(4),

      "&&&.MuiInputBase-adornedStart": {
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
      },
    }),
  },
};
